import * as React from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  Add as AddIcon,
  VideoFile as VideoFileIcon,
  DataArray as DataArrayIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Import our create components
import CreateNewEvent from './CreateNewEvent';
import CreateNewSession from './CreateNewSession';

// GraphQL imports
import { API } from 'aws-amplify';
import { listEventLists, listSessionLists } from '../../graphql/queries';
import { createVideoData } from '../../graphql/mutations';
import { useAuthenticator } from '@aws-amplify/ui-react';

// Import GlobalContext for activeProfile
import { GlobalContext } from '../GlobalState';

// Import fetchUserProfiles
import { fetchUserProfiles } from '../shared/fetchProfiles';

export default function SessionUploadManager({ onClose }) {
  // Get user data
  const { user } = useAuthenticator((context) => [context.user]);
  const { activeProfile } = React.useContext(GlobalContext);

  // Tab state
  const [tabValue, setTabValue] = React.useState(0);

  // File/URL state
  const [selectedFiles, setSelectedFiles] = React.useState({
    video: null,
    telemetry: null,
    laptime: null,
  });
  const [videoUrl, setVideoUrl] = React.useState('');

  // Telemetry data state
  const [telemetryData, setTelemetryData] = React.useState(null);
  const [telemetryError, setTelemetryError] = React.useState('');
  const [loadingTelemetry, setLoadingTelemetry] = React.useState(false);

  // Server browser state
  const [baseServerUrl, setBaseServerUrl] = React.useState(
    'https://localhost:8080/'
  );
  const [currentPath, setCurrentPath] = React.useState('');
  const [serverFiles, setServerFiles] = React.useState([]);
  const [serverFolders, setServerFolders] = React.useState([]);
  const [selectedServerFiles, setSelectedServerFiles] = React.useState({
    video: null,
    telemetry: null,
    laptime: null,
  });
  const [loadingServerFiles, setLoadingServerFiles] = React.useState(false);
  const [serverError, setServerError] = React.useState('');
  const [urlInputMode, setUrlInputMode] = React.useState('manual'); // 'manual' or 'browse'
  const [breadcrumbs, setBreadcrumbs] = React.useState([]);

  // Selection state
  const [selectedEvent, setSelectedEvent] = React.useState('');
  const [selectedSession, setSelectedSession] = React.useState('');
  const [selectedDriver, setSelectedDriver] = React.useState('');
  const [selectedCarNumber, setSelectedCarNumber] = React.useState('');

  // VideoData state with defaults
  const [cameraAngle] = React.useState('front');
  const [greenFlag] = React.useState(0);
  const [crossStart, setCrossStart] = React.useState(0);

  // Data state
  const [events, setEvents] = React.useState([]);
  const [sessions, setSessions] = React.useState([]);
  const [userProfiles, setUserProfiles] = React.useState([]);

  // Modal state
  const [createEventOpen, setCreateEventOpen] = React.useState(false);
  const [createSessionOpen, setCreateSessionOpen] = React.useState(false);
  const [createDriverOpen, setCreateDriverOpen] = React.useState(false);
  const [customDriverName, setCustomDriverName] = React.useState('');

  // Loading state
  const [isLoading, setIsLoading] = React.useState(false);

  // Reset state function
  const resetState = () => {
    setTabValue(0);
    setSelectedFiles({ video: null, telemetry: null, laptime: null });
    setVideoUrl('');
    setCurrentPath('');
    setServerFiles([]);
    setServerFolders([]);
    setSelectedServerFiles({ video: null, telemetry: null, laptime: null });
    setLoadingServerFiles(false);
    setServerError('');
    setUrlInputMode('manual');
    setBreadcrumbs([]);
    setSelectedEvent('');
    setSelectedSession('');
    setSelectedDriver('');
    setSelectedCarNumber('');
    setSessions([]);
    setCreateEventOpen(false);
    setCreateSessionOpen(false);
    setCreateDriverOpen(false);
    setCustomDriverName('');
    setIsLoading(false);
    setCrossStart(0);
    clearTelemetryData();
  };

  const handleCancel = () => {
    resetState();
    if (onClose) {
      onClose();
    }
  };

  // Load events and user profiles on component mount
  React.useEffect(() => {
    loadEvents();
    loadUserProfiles();
  }, []);

  // Load sessions when event changes
  React.useEffect(() => {
    if (selectedEvent) {
      loadSessions(selectedEvent);
    }
  }, [selectedEvent]);

  // Default driver to current user when profiles are loaded
  React.useEffect(() => {
    if (user && userProfiles.length > 0 && !selectedDriver) {
      const currentUserProfile = userProfiles.find(
        (profile) => profile.username === user.username
      );
      if (currentUserProfile) {
        setSelectedDriver(user.username);
      }
    }
  }, [user, userProfiles, selectedDriver]);

  const loadEvents = async () => {
    try {
      const result = await API.graphql({
        query: listEventLists,
        variables: {
          filter: {
            eventSource: { eq: 'manual' },
          },
        },
      });
      setEvents(result.data.listEventLists.items || []);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const loadSessions = async (eventId) => {
    try {
      const result = await API.graphql({
        query: listSessionLists,
        variables: {
          filter: {
            eventId: { eq: eventId },
          },
        },
      });
      setSessions(result.data.listSessionLists.items || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadUserProfiles = async () => {
    try {
      const profiles = await fetchUserProfiles();
      setUserProfiles(profiles || []);
    } catch (error) {
      console.error('Error loading user profiles:', error);
    }
  };

  const parseDirectoryListing = (html, pathForUrls) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = doc.querySelectorAll('a');
    const files = [];
    const folders = [];

    links.forEach((link) => {
      const href = link.getAttribute('href');
      const text = link.textContent.trim();

      // Skip parent directory and empty links
      if (
        href &&
        href !== '../' &&
        href !== '/' &&
        text &&
        text !== 'Parent Directory'
      ) {
        // Check if it's a folder (ends with /)
        if (href.endsWith('/')) {
          folders.push({
            name: text,
            href: href,
            type: 'folder',
          });
        } else {
          // Determine file type
          const isVideo = /\.(mp4|avi|mov|mkv|webm|m4v)$/i.test(href);
          const isTelemetry = /\.(json|txt)$/i.test(href);
          const isLaptime = /\.csv$/i.test(href);

          if (isVideo || isTelemetry || isLaptime) {
            // Construct the full URL properly using the passed path
            let fullUrl;
            if (baseServerUrl.endsWith('/')) {
              fullUrl = baseServerUrl + pathForUrls + href;
            } else {
              fullUrl = baseServerUrl + '/' + pathForUrls + href;
            }

            files.push({
              name: text,
              href: href,
              type: isVideo ? 'video' : isTelemetry ? 'telemetry' : 'laptime',
              fullUrl: fullUrl,
            });
          }
        }
      }
    });

    return { files, folders };
  };

  const loadServerFiles = async (path = '') => {
    const targetUrl = baseServerUrl + path;

    setLoadingServerFiles(true);
    setServerError('');

    try {
      const response = await fetch(targetUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      const { files, folders } = parseDirectoryListing(html, path);

      setServerFiles(files);
      setServerFolders(folders);
      setCurrentPath(path);

      // Update breadcrumbs
      if (path === '') {
        setBreadcrumbs([]);
      } else {
        const pathParts = path.split('/').filter((part) => part !== '');
        const newBreadcrumbs = pathParts.map((part, index) => ({
          name: part,
          path: pathParts.slice(0, index + 1).join('/') + '/',
        }));
        setBreadcrumbs(newBreadcrumbs);
      }

      if (files.length === 0 && folders.length === 0) {
        setServerError('No files or folders found in this directory');
      }
    } catch (error) {
      setServerError(`Error loading directory: ${error.message}`);
      setServerFiles([]);
      setServerFolders([]);
    } finally {
      setLoadingServerFiles(false);
    }
  };

  const handleServerFileSelect = (file) => {
    setSelectedServerFiles((prev) => ({
      ...prev,
      [file.type]: file,
    }));

    // If it's a telemetry file, parse it for preview
    if (file.type === 'telemetry') {
      parseTelemetryUrl(file.fullUrl);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear selections when switching tabs
    if (newValue === 0) {
      setVideoUrl('');
      setSelectedServerFiles({ video: null, telemetry: null, laptime: null });
      setServerFiles([]);
      setServerFolders([]);
      setCurrentPath('');
      setBreadcrumbs([]);
      // Keep laptime file from URL mode if it exists
    } else {
      // Keep laptime file when switching to URL mode, clear others
      setSelectedFiles((prev) => ({
        video: null,
        telemetry: null,
        laptime: prev.laptime, // Keep laptime file
      }));
    }
    // Clear telemetry data when switching tabs
    clearTelemetryData();
  };

  const handleFileSelect = (event, fileType) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles((prev) => ({
        ...prev,
        [fileType]: file,
      }));

      // If it's a telemetry file, parse it for preview
      if (fileType === 'telemetry') {
        parseTelemetryFile(file);
      }
    } else if (fileType === 'telemetry') {
      // If no file selected, clear telemetry data
      clearTelemetryData();
    }
  };

  // CSV parsing function
  const parseCSVToJSON = (csvText, fileName) => {
    const lines = csvText.split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) return null;

    const headers = lines[0].split(',').map((header) => header.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((value) => value.trim());
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });
        data.push(row);
      }
    }

    return {
      headers,
      data,
      metadata: {
        fileName,
        rowCount: data.length,
        importDate: new Date().toISOString(),
      },
    };
  };

  // Format data for VideoData table
  const formatVideoDataForDb = async () => {
    // Generate standardized filename for file uploads
    const generateStandardizedFilename = (originalFile) => {
      if (!originalFile) return null;
      const fileExtension = originalFile.name.split('.').pop();
      return `${selectedEvent}_${selectedSession}_${selectedCarNumber}_${cameraAngle}_${user.username}_video.${fileExtension}`;
    };

    // Build the VideoData object
    const videoData = {
      username: user.username,
      drivername: selectedDriver, // Add the drivername field
      carNum: selectedCarNumber,
      cameraAngle: cameraAngle,
      greenFlag: greenFlag,
      crossStart: crossStart,
      eventId: selectedEvent,
      sessionId: selectedSession,
      vidUploadInitiated: false,
      // Initialize all telemetry fields as null - we'll set the appropriate one below
      telemetryS3Key: null,
      tempLocalTelemetryLink: null,
      tracksideTelemetryUrl: null,
      groups: activeProfile?.memberOf || [], // Use activeProfile.memberOf or empty array as fallback
      laptimeData: null, // Will be set below if laptime file is selected
    };

    // Handle laptime data for both file and URL modes
    if (selectedFiles.laptime) {
      // Parse CSV file (can be from file picker in either mode)
      const csvText = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsText(selectedFiles.laptime);
      });
      const jsonData = parseCSVToJSON(csvText, selectedFiles.laptime.name);
      videoData.laptimeData = JSON.stringify(jsonData);
    } else if (
      tabValue === 1 &&
      urlInputMode === 'browse' &&
      selectedServerFiles.laptime
    ) {
      // URL mode with server file - fetch and parse CSV from server
      try {
        const response = await fetch(selectedServerFiles.laptime.fullUrl);
        if (response.ok) {
          const csvText = await response.text();
          const jsonData = parseCSVToJSON(
            csvText,
            selectedServerFiles.laptime.name
          );
          videoData.laptimeData = JSON.stringify(jsonData);
        }
      } catch (error) {
        console.error('Error fetching laptime data from server:', error);
      }
    }

    if (tabValue === 0) {
      // File mode
      videoData.vidSource = selectedFiles.video?.name || null;
      videoData.srcVideoFormattedName = generateStandardizedFilename(
        selectedFiles.video
      );
      videoData.tempLocalVideoLink = selectedFiles.video
        ? URL.createObjectURL(selectedFiles.video)
        : null;
      videoData.youtubeStreamUrl = null;

      // Handle local telemetry file
      if (selectedFiles.telemetry) {
        videoData.tempLocalTelemetryLink = URL.createObjectURL(
          selectedFiles.telemetry
        );
      }
    } else {
      // URL mode
      if (urlInputMode === 'manual') {
        videoData.youtubeStreamUrl = videoUrl;
        videoData.vidSource = null;
        videoData.srcVideoFormattedName = null;
        videoData.tempLocalVideoLink = null;
      } else {
        // Browse server mode
        videoData.tracksideVideoUrl =
          selectedServerFiles.video?.fullUrl || null;
        videoData.vidSource = selectedServerFiles.video?.name || null;
        videoData.srcVideoFormattedName = null;
        videoData.youtubeStreamUrl = null;
        videoData.tempLocalVideoLink = null;

        // Handle server telemetry file
        if (selectedServerFiles.telemetry) {
          videoData.tracksideTelemetryUrl =
            selectedServerFiles.telemetry.fullUrl;
        }
      }
    }

    return videoData;
  };

  const handleSubmit = async () => {
    // Basic validation
    if (
      !selectedEvent ||
      !selectedSession ||
      !selectedDriver ||
      !selectedCarNumber
    ) {
      alert('Please fill in all required fields');
      return;
    }

    if (tabValue === 0 && !selectedFiles.video) {
      alert('Please select a video file');
      return;
    }

    if (tabValue === 1) {
      if (urlInputMode === 'manual' && !videoUrl) {
        alert('Please enter a video URL');
        return;
      }
      if (urlInputMode === 'browse' && !selectedServerFiles.video) {
        alert('Please select a video file from the server');
        return;
      }
    }

    setIsLoading(true);

    try {
      // Format data for VideoData table
      const videoData = await formatVideoDataForDb();
      console.log('VideoData to be created:', videoData);

      // Create VideoData record
      const response = await API.graphql({
        query: createVideoData,
        variables: { input: videoData },
      });

      console.log(
        'VideoData created successfully:',
        response.data.createVideoData
      );
      alert('Session uploaded successfully!');

      // Close the manager or reset form
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error creating VideoData record:', error);
      alert('Error uploading session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEventCreated = async (result) => {
    setCreateEventOpen(false);
    await loadEvents(); // Reload events

    // Auto-select the newly created event
    const newEventId = result?.data?.createEventList?.id;
    if (newEventId) {
      setSelectedEvent(newEventId);
    }
  };

  const handleSessionCreated = async (result) => {
    setCreateSessionOpen(false);
    if (selectedEvent) {
      await loadSessions(selectedEvent); // Reload sessions for current event
    }

    // Auto-select the newly created session
    const newSessionId = result?.data?.createSessionList?.id;
    if (newSessionId) {
      setSelectedSession(newSessionId);
    }
  };

  const handleCustomDriverSubmit = () => {
    if (customDriverName.trim()) {
      setSelectedDriver(customDriverName.trim());
      setCreateDriverOpen(false);
      setCustomDriverName('');
    }
  };

  const handleCustomDriverCancel = () => {
    setCreateDriverOpen(false);
    setCustomDriverName('');
  };

  const handleFolderClick = (folder) => {
    const newPath = currentPath + folder.href;
    loadServerFiles(newPath);
  };

  const handleBreadcrumbClick = (path) => {
    loadServerFiles(path);
  };

  const handleBackToRoot = () => {
    loadServerFiles();
  };

  // Telemetry parsing functions
  const parseTelemetryFile = async (file) => {
    setLoadingTelemetry(true);
    setTelemetryError('');
    setTelemetryData(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Debug logging to see what's actually in the data
      console.log('FILE Telemetry data keys:', Object.keys(data));
      console.log('FILE telemetry_system data:', data.telemetry_system);

      // Check if this is HtrLapTimer telemetry and auto-populate form values
      if (data.telemetry_system?.name === 'HtrLapTimer' && data.laps) {
        // Find lapNumber 0 to get crossStart value
        const lap0 = data.laps.find((lap) => lap.lapNumber === 0);
        if (lap0 && lap0.duration) {
          // Convert from milliseconds to seconds
          const crossStartSeconds = lap0.duration / 1000;
          setCrossStart(crossStartSeconds);
          console.log(
            `Auto-populated crossStart: ${crossStartSeconds} seconds from lap 0 duration`
          );
        }
      }

      // Extract the key sections we want to display
      const telemetryPreview = {
        car: data.car || null,
        session: data.session || null,
        user: data.user || null,
        laps: data.laps || null,
        telemetry_system: data.telemetry_system || null,
        locationsCount: data.locations ? data.locations.length : 0,
        fileSize: file.size,
        fileName: file.name,
      };

      console.log('FILE Extracted telemetry preview:', telemetryPreview);

      setTelemetryData(telemetryPreview);
    } catch (error) {
      setTelemetryError(`Error parsing telemetry file: ${error.message}`);
    } finally {
      setLoadingTelemetry(false);
    }
  };

  const parseTelemetryUrl = async (url) => {
    setLoadingTelemetry(true);
    setTelemetryError('');
    setTelemetryData(null);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      const data = JSON.parse(text);

      // Debug logging to see what's actually in the data
      console.log('URL Telemetry data keys:', Object.keys(data));
      console.log('telemetry_system data:', data.telemetry_system);
      console.log('Full data structure:', data);

      // Check if this is HtrLapTimer telemetry and auto-populate form values
      if (data.telemetry_system?.name === 'HtrLapTimer' && data.laps) {
        // Find lapNumber 0 to get crossStart value
        const lap0 = data.laps.find((lap) => lap.lapNumber === 0);
        if (lap0 && lap0.duration) {
          // Convert from milliseconds to seconds
          const crossStartSeconds = lap0.duration / 1000;
          setCrossStart(crossStartSeconds);
          console.log(
            `Auto-populated crossStart: ${crossStartSeconds} seconds from lap 0 duration`
          );
        }
      }

      // Extract the key sections we want to display
      const telemetryPreview = {
        car: data.car || null,
        session: data.session || null,
        user: data.user || null,
        laps: data.laps || null,
        telemetry_system: data.telemetry_system || null,
        locationsCount: data.locations ? data.locations.length : 0,
        fileSize: text.length,
        fileName: url.split('/').pop(),
      };

      console.log('Extracted telemetry preview:', telemetryPreview);

      setTelemetryData(telemetryPreview);
    } catch (error) {
      setTelemetryError(`Error loading telemetry from URL: ${error.message}`);
    } finally {
      setLoadingTelemetry(false);
    }
  };

  const clearTelemetryData = () => {
    setTelemetryData(null);
    setTelemetryError('');
    setLoadingTelemetry(false);
    setCrossStart(0);
  };

  // Telemetry Debug component to show telemetry data sections
  const TelemetryDebugPanel = () => {
    if (!telemetryData && !telemetryError && !loadingTelemetry) {
      return null;
    }

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Telemetry Data Preview
          </Typography>

          {loadingTelemetry && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading telemetry data...</Typography>
            </Box>
          )}

          {telemetryError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {telemetryError}
            </Alert>
          )}

          {telemetryData && (
            <Grid container spacing={2}>
              {/* Telemetry System Info */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Telemetry System:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: telemetryData.telemetry_system
                      ? 'primary.light'
                      : 'grey.100',
                    color: telemetryData.telemetry_system
                      ? 'primary.contrastText'
                      : 'text.primary',
                    p: 2,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 150,
                  }}
                >
                  {telemetryData.telemetry_system
                    ? JSON.stringify(telemetryData.telemetry_system, null, 2)
                    : 'No telemetry system info found'}
                </Box>
              </Grid>

              {/* File Info */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  File Information:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: 'info.light',
                    color: 'info.contrastText',
                    p: 2,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 150,
                  }}
                >
                  {JSON.stringify(
                    {
                      fileName: telemetryData.fileName,
                      fileSize: `${(
                        telemetryData.fileSize /
                        1024 /
                        1024
                      ).toFixed(2)} MB`,
                      locationsCount: telemetryData.locationsCount,
                    },
                    null,
                    2
                  )}
                </Box>
              </Grid>

              {/* Car Data */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Car Data:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: telemetryData.car
                      ? 'success.light'
                      : 'grey.100',
                    color: telemetryData.car
                      ? 'success.contrastText'
                      : 'text.primary',
                    p: 2,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 150,
                  }}
                >
                  {telemetryData.car
                    ? JSON.stringify(telemetryData.car, null, 2)
                    : 'No car data found'}
                </Box>
              </Grid>

              {/* Session Data */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Session Data:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: telemetryData.session
                      ? 'success.light'
                      : 'grey.100',
                    color: telemetryData.session
                      ? 'success.contrastText'
                      : 'text.primary',
                    p: 2,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 150,
                  }}
                >
                  {telemetryData.session
                    ? JSON.stringify(telemetryData.session, null, 2)
                    : 'No session data found'}
                </Box>
              </Grid>

              {/* User Data */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  User Data:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: telemetryData.user
                      ? 'success.light'
                      : 'grey.100',
                    color: telemetryData.user
                      ? 'success.contrastText'
                      : 'text.primary',
                    p: 2,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 150,
                  }}
                >
                  {telemetryData.user
                    ? JSON.stringify(telemetryData.user, null, 2)
                    : 'No user data found'}
                </Box>
              </Grid>

              {/* Laps Data */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Laps Data (
                  {telemetryData.laps ? telemetryData.laps.length : 0} laps):
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: telemetryData.laps
                      ? 'success.light'
                      : 'grey.100',
                    color: telemetryData.laps
                      ? 'success.contrastText'
                      : 'text.primary',
                    p: 2,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 200,
                  }}
                >
                  {telemetryData.laps
                    ? JSON.stringify(telemetryData.laps, null, 2)
                    : 'No laps data found'}
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    );
  };

  // Debug component to show current state
  const DebugPanel = () => {
    const currentState = {
      selectedEvent,
      selectedSession,
      selectedDriver,
      selectedCarNumber,
      tabValue,
      urlInputMode,
      videoUrl,
      selectedFiles: {
        video: selectedFiles.video
          ? {
              name: selectedFiles.video.name,
              size: selectedFiles.video.size,
              type: selectedFiles.video.type,
            }
          : null,
        telemetry: selectedFiles.telemetry
          ? {
              name: selectedFiles.telemetry.name,
              size: selectedFiles.telemetry.size,
              type: selectedFiles.telemetry.type,
            }
          : null,
        laptime: selectedFiles.laptime
          ? {
              name: selectedFiles.laptime.name,
              size: selectedFiles.laptime.size,
              type: selectedFiles.laptime.type,
            }
          : null,
      },
      selectedServerFiles,
      trackInfo: '',
      cameraAngle,
      greenFlag,
      crossStart,
      user: user ? { username: user.username } : null,
      activeProfile: activeProfile
        ? {
            memberOf: activeProfile.memberOf,
            username: activeProfile.username,
          }
        : null,
      userProfilesCount: userProfiles.length,
    };

    let videoDataPreview = null;
    try {
      if (selectedEvent && selectedSession && selectedCarNumber && user) {
        videoDataPreview = formatVideoDataForDb();
      }
    } catch (error) {
      videoDataPreview = { error: error.message };
    }

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Debug Panel
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Current Form State:
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 300,
                }}
              >
                {JSON.stringify(currentState, null, 2)}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                VideoData Object (to be written to DB):
              </Typography>
              <Box
                component="pre"
                sx={{
                  backgroundColor: videoDataPreview?.error
                    ? 'error.light'
                    : 'success.light',
                  color: videoDataPreview?.error
                    ? 'error.contrastText'
                    : 'success.contrastText',
                  p: 2,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: 300,
                }}
              >
                {videoDataPreview
                  ? JSON.stringify(videoDataPreview, null, 2)
                  : 'Fill out required fields to see VideoData preview'}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Session Upload Manager
        </Typography>

        <Grid container spacing={4}>
          {/* Left Side - File/URL Selection */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  sx={{ mb: 3 }}
                >
                  <Tab icon={<CloudUploadIcon />} label="File Picker" />
                  <Tab icon={<LinkIcon />} label="URL Picker" />
                </Tabs>

                {tabValue === 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Select Files
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          sx={{ height: 120, flexDirection: 'column' }}
                        >
                          <VideoFileIcon sx={{ fontSize: 40, mb: 1 }} />
                          Select Video File
                          <input
                            type="file"
                            hidden
                            accept="video/*"
                            onChange={(e) => handleFileSelect(e, 'video')}
                          />
                        </Button>
                        {selectedFiles.video && (
                          <Chip
                            label={selectedFiles.video.name}
                            sx={{ mt: 1 }}
                            color="primary"
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          sx={{ height: 120, flexDirection: 'column' }}
                        >
                          <DataArrayIcon sx={{ fontSize: 40, mb: 1 }} />
                          Select Telemetry File (Optional)
                          <input
                            type="file"
                            hidden
                            accept=".json,.txt"
                            onChange={(e) => handleFileSelect(e, 'telemetry')}
                          />
                        </Button>
                        {selectedFiles.telemetry && (
                          <Chip
                            label={selectedFiles.telemetry.name}
                            sx={{ mt: 1 }}
                            color="secondary"
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          sx={{ height: 120, flexDirection: 'column' }}
                        >
                          <FileIcon sx={{ fontSize: 40, mb: 1 }} />
                          Select Laptime File (Optional)
                          <input
                            type="file"
                            hidden
                            accept=".csv"
                            onChange={(e) => handleFileSelect(e, 'laptime')}
                          />
                        </Button>
                        {selectedFiles.laptime && (
                          <Chip
                            label={selectedFiles.laptime.name}
                            sx={{ mt: 1 }}
                            color="info"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {tabValue === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Video URL Options
                    </Typography>

                    <Tabs
                      value={urlInputMode}
                      onChange={(e, v) => setUrlInputMode(v)}
                      sx={{ mb: 3 }}
                    >
                      <Tab value="manual" label="Manual URL" />
                      <Tab value="browse" label="Trackside Server" />
                    </Tabs>

                    {urlInputMode === 'manual' && (
                      <Box>
                        <TextField
                          fullWidth
                          label="Video URL (YouTube, Vimeo, etc.)"
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          sx={{ mb: 3 }}
                        />

                        {/* Laptime File Picker for Manual URL Mode */}
                        <Typography variant="subtitle1" gutterBottom>
                          Optional Files
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Button
                              variant="outlined"
                              component="label"
                              fullWidth
                              sx={{ height: 100, flexDirection: 'column' }}
                            >
                              <FileIcon sx={{ fontSize: 32, mb: 1 }} />
                              Select Laptime File (Optional)
                              <input
                                type="file"
                                hidden
                                accept=".csv"
                                onChange={(e) => handleFileSelect(e, 'laptime')}
                              />
                            </Button>
                            {selectedFiles.laptime && (
                              <Chip
                                label={selectedFiles.laptime.name}
                                sx={{ mt: 1 }}
                                color="info"
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {urlInputMode === 'browse' && (
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <TextField
                            fullWidth
                            label="Base Server URL"
                            value={baseServerUrl}
                            onChange={(e) => setBaseServerUrl(e.target.value)}
                            placeholder="http://127.0.0.1:8080/2025-05-31-23-22/"
                          />
                          <Button
                            variant="outlined"
                            onClick={() => loadServerFiles()}
                            disabled={loadingServerFiles}
                            sx={{ minWidth: 'auto' }}
                          >
                            {loadingServerFiles ? (
                              <CircularProgress size={24} />
                            ) : (
                              <RefreshIcon />
                            )}
                          </Button>
                        </Box>

                        {serverError && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {serverError}
                          </Alert>
                        )}

                        {/* Breadcrumbs */}
                        {breadcrumbs.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Path:
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Button
                                size="small"
                                onClick={handleBackToRoot}
                                sx={{ minWidth: 'auto', textTransform: 'none' }}
                              >
                                Root
                              </Button>
                              {breadcrumbs.map((crumb, index) => (
                                <React.Fragment key={index}>
                                  <Typography>/</Typography>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      handleBreadcrumbClick(crumb.path)
                                    }
                                    sx={{
                                      minWidth: 'auto',
                                      textTransform: 'none',
                                    }}
                                  >
                                    {crumb.name}
                                  </Button>
                                </React.Fragment>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {(serverFolders.length > 0 ||
                          serverFiles.length > 0) && (
                          <Box>
                            <Typography variant="subtitle1" gutterBottom>
                              {serverFolders.length > 0 &&
                              serverFiles.length > 0
                                ? 'Folders & Files:'
                                : serverFolders.length > 0
                                ? 'Folders:'
                                : 'Available Files:'}
                            </Typography>
                            <List dense>
                              {/* Show folders first */}
                              {serverFolders.map((folder, index) => (
                                <ListItem
                                  key={`folder-${index}`}
                                  disablePadding
                                >
                                  <ListItemButton
                                    onClick={() => handleFolderClick(folder)}
                                  >
                                    <ListItemIcon>
                                      <FolderIcon color="action" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={folder.name}
                                      secondary="folder"
                                    />
                                  </ListItemButton>
                                </ListItem>
                              ))}

                              {/* Then show files */}
                              {serverFiles.map((file, index) => (
                                <ListItem key={`file-${index}`} disablePadding>
                                  <ListItemButton
                                    onClick={() => handleServerFileSelect(file)}
                                    selected={
                                      selectedServerFiles[file.type]?.name ===
                                      file.name
                                    }
                                  >
                                    <ListItemIcon>
                                      {file.type === 'video' ? (
                                        <VideoFileIcon color="primary" />
                                      ) : file.type === 'telemetry' ? (
                                        <DataArrayIcon color="secondary" />
                                      ) : (
                                        <FileIcon color="info" />
                                      )}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={file.name}
                                      secondary={`${file.type} • ${file.fullUrl}`}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                )}

                {/* File Details Section */}
                {(selectedFiles.video ||
                  selectedFiles.telemetry ||
                  selectedFiles.laptime ||
                  videoUrl ||
                  selectedServerFiles.video ||
                  selectedServerFiles.telemetry ||
                  selectedServerFiles.laptime) && (
                  <Box
                    sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Selected File/URL Details
                    </Typography>
                    {tabValue === 0 ? (
                      <>
                        {selectedFiles.video && (
                          <Typography>
                            Video filename: {selectedFiles.video.name}
                          </Typography>
                        )}
                        {selectedFiles.telemetry && (
                          <Typography>
                            Telemetry filename: {selectedFiles.telemetry.name}
                          </Typography>
                        )}
                        {selectedFiles.laptime && (
                          <Typography>
                            Laptime filename: {selectedFiles.laptime.name}
                          </Typography>
                        )}
                      </>
                    ) : urlInputMode === 'manual' ? (
                      <>
                        <Typography>Video URL: {videoUrl}</Typography>
                        {selectedFiles.laptime && (
                          <Typography>
                            Laptime filename: {selectedFiles.laptime.name}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        {selectedServerFiles.video && (
                          <Typography>
                            Video URL: {selectedServerFiles.video.fullUrl}
                          </Typography>
                        )}
                        {selectedServerFiles.telemetry && (
                          <Typography>
                            Telemetry URL:{' '}
                            {selectedServerFiles.telemetry.fullUrl}
                          </Typography>
                        )}
                        {selectedServerFiles.laptime && (
                          <Typography>
                            Laptime URL: {selectedServerFiles.laptime.fullUrl}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Event/Session/Driver Selection */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Session Details
                </Typography>

                {/* Event Selection */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Select Event</InputLabel>
                      <Select
                        value={selectedEvent}
                        label="Select Event"
                        onChange={(e) => setSelectedEvent(e.target.value)}
                      >
                        {events.map((event) => (
                          <MenuItem key={event.id} value={event.id}>
                            {event.eventName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={() => setCreateEventOpen(true)}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>
                </Box>

                {/* Session Selection */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <FormControl fullWidth disabled={!selectedEvent}>
                      <InputLabel>Select Session</InputLabel>
                      <Select
                        value={selectedSession}
                        label="Select Session"
                        onChange={(e) => setSelectedSession(e.target.value)}
                      >
                        {sessions.map((session) => (
                          <MenuItem key={session.id} value={session.id}>
                            {session.sessionName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      disabled={!selectedEvent}
                      onClick={() => setCreateSessionOpen(true)}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>
                </Box>

                {/* Driver Selection */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Select Driver</InputLabel>
                      <Select
                        value={selectedDriver}
                        label="Select Driver"
                        onChange={(e) => setSelectedDriver(e.target.value)}
                      >
                        {userProfiles.map((profile) => (
                          <MenuItem key={profile.id} value={profile.username}>
                            {profile.name} ({profile.username})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={() => setCreateDriverOpen(true)}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>
                </Box>

                {/* Car Number */}
                <TextField
                  fullWidth
                  label="Car Number"
                  value={selectedCarNumber}
                  onChange={(e) => setSelectedCarNumber(e.target.value)}
                  sx={{ mb: 3 }}
                />

                {/* Submit Button */}
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                >
                  {isLoading ? 'Submitting...' : 'Submit'}
                </Button>

                {/* Cancel Button */}
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  size="large"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Create Event Modal */}
        <Dialog
          open={createEventOpen}
          onClose={() => setCreateEventOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent>
            <CreateNewEvent onEventCreated={handleEventCreated} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateEventOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Create Session Modal */}
        <Dialog
          open={createSessionOpen}
          onClose={() => setCreateSessionOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent>
            {selectedEvent ? (
              <CreateNewSession
                defaultEventId={selectedEvent}
                onSessionCreated={handleSessionCreated}
              />
            ) : (
              <Typography color="error">
                Please select an event first
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateSessionOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Create Custom Driver Modal */}
        <Dialog
          open={createDriverOpen}
          onClose={handleCustomDriverCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Custom Driver Name</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the name of a driver who doesn't have an account in the
              system.
            </Typography>
            <TextField
              fullWidth
              label="Driver Name"
              value={customDriverName}
              onChange={(e) => setCustomDriverName(e.target.value)}
              placeholder="Enter driver's full name"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCustomDriverSubmit();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCustomDriverCancel}>Cancel</Button>
            <Button
              onClick={handleCustomDriverSubmit}
              variant="contained"
              disabled={!customDriverName.trim()}
            >
              Add Driver
            </Button>
          </DialogActions>
        </Dialog>

        <TelemetryDebugPanel />
        <DebugPanel />
      </Paper>
    </Container>
  );
}
